import { useEffect, useState } from 'react';
import TitleBar from './components/ui/TitleBar';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

const API_BASE = getApiBase();
const EMPTY_FORM = {
  unit_number: '',
  year: '',
  make: '',
  model: '',
  color: '',
  license_plate_number: '',
  is_active: true
};
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const VehiclesSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile());
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVehicles = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/settings/vehicles`, {
        headers: { ...getTenantHeaders() }
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Load failed (HTTP ${response.status})`);
      }
      setVehicles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load vehicles:', error);
      setVehicles([]);
      alert('Failed to load vehicles.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm(EMPTY_FORM);

  const validateForm = () => {
    const unitNumber = form.unit_number.trim();
    if (!unitNumber) {
      alert('UNIT # IS REQUIRED.');
      return null;
    }

    const yearText = String(form.year || '').trim();
    if (yearText) {
      const maxVehicleYear = new Date().getFullYear() + 1;
      const year = Number(yearText);
      if (!/^\d+$/.test(yearText) || !Number.isInteger(year) || year < 1900 || year > maxVehicleYear) {
        alert(`YEAR MUST BE BETWEEN 1900 AND ${maxVehicleYear}.`);
        return null;
      }
    }

    return {
      unit_number: unitNumber,
      year: yearText || null,
      make: form.make.trim(),
      model: form.model.trim(),
      color: form.color.trim(),
      license_plate_number: form.license_plate_number.trim(),
      is_active: form.is_active
    };
  };

  const closeForm = () => {
    setShowAddVehicle(false);
    setEditingVehicleId(null);
    resetForm();
  };

  const createVehicle = async () => {
    const payload = validateForm();
    if (!payload) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/settings/vehicles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Save failed (HTTP ${response.status})`);
      }
      closeForm();
      await loadVehicles();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'VEHICLE CREATED!' }
      }));
    } catch (error) {
      console.error('Failed to create vehicle:', error);
      alert(error.message || 'Failed to save vehicle.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (vehicle) => {
    setShowAddVehicle(false);
    setEditingVehicleId(vehicle.id);
    setForm({
      unit_number: vehicle.unit_number || '',
      year: vehicle.year == null ? '' : String(vehicle.year),
      make: vehicle.make || '',
      model: vehicle.model || '',
      color: vehicle.color || '',
      license_plate_number: vehicle.license_plate_number || '',
      is_active: Number(vehicle.is_active) === 1
    });
  };

  const updateVehicle = async () => {
    const payload = validateForm();
    if (!payload) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/settings/vehicles/${editingVehicleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Update failed (HTTP ${response.status})`);
      }
      closeForm();
      await loadVehicles();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'VEHICLE UPDATED!' }
      }));
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      alert(error.message || 'Failed to update vehicle.');
    } finally {
      setSaving(false);
    }
  };

  const deleteVehicle = async (vehicle) => {
    if (!window.confirm(`DELETE VEHICLE UNIT # "${vehicle.unit_number}"?`)) return;

    try {
      const response = await fetch(`${API_BASE}/api/settings/vehicles/${vehicle.id}`, {
        method: 'DELETE',
        headers: { ...getTenantHeaders() }
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Delete failed (HTTP ${response.status})`);
      }
      await loadVehicles();
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
      alert(error.message || 'Failed to delete vehicle.');
    }
  };

  const openAddForm = () => {
    setEditingVehicleId(null);
    resetForm();
    setShowAddVehicle(true);
  };

  const styles = {
    page: { padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'), backgroundColor: '#E8E2DA', minHeight: '100vh' },
    contentWrapper: { maxWidth: '1200px', padding: isPhone ? '0' : '0 12px', margin: '0 auto' },
    panel: { backgroundColor: '#E7B888', border: isMobile ? '6px solid black' : '10px solid black', padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '40px'), marginBottom: '18px' },
    note: { fontWeight: 900, fontSize: '13px', padding: '12px', border: '3px solid black', backgroundColor: '#ffffcc', marginBottom: '12px' },
    addBtn: { background: 'black', color: '#FFA500', border: '4px solid #E8761D', padding: '12px 20px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', fontSize: '14px', marginBottom: '12px' },
    card: { border: '4px solid black', padding: '14px', marginBottom: '12px', backgroundColor: 'white' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' },
    cardInfo: { flex: 1, minWidth: '200px' },
    cardTitle: { fontWeight: 900, fontSize: '20px' },
    vehicleDetails: { fontWeight: 700, fontSize: '14px', lineHeight: 1.5, marginTop: '8px', overflowWrap: 'anywhere' },
    status: { display: 'inline-block', marginTop: '8px', padding: '4px 8px', border: '2px solid black', fontWeight: 900, fontSize: '12px' },
    cardActions: { display: 'flex', gap: '8px' },
    editBtn: { background: 'white', color: 'black', border: '3px solid black', padding: '8px 14px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', fontSize: '13px' },
    deleteBtn: { background: '#ff4444', color: 'white', border: '3px solid black', padding: '8px 14px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', fontSize: '13px' },
    loadingBox: { padding: '30px', textAlign: 'center', fontWeight: 900, fontSize: '16px' },
    modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000, padding: '20px' },
    modalContent: { backgroundColor: 'white', border: '8px solid black', padding: '24px', maxWidth: '650px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
    modalTitle: { fontWeight: 900, fontSize: '22px', marginBottom: '16px', textTransform: 'uppercase' },
    formGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' },
    fullRow: { gridColumn: isMobile ? 'auto' : 'span 2' },
    row: { marginBottom: '4px' },
    label: { fontWeight: 900, textTransform: 'uppercase', fontSize: '13px', marginBottom: '6px', display: 'block' },
    input: { padding: '14px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box', backgroundColor: 'white' },
    checkboxRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', border: '4px solid black', fontWeight: 900, cursor: 'pointer', boxSizing: 'border-box' },
    modalBtnRow: { display: 'flex', gap: '10px', marginTop: '16px' },
    confirmBtn: { flex: 1, background: 'black', color: '#FFA500', border: '4px solid #E8761D', padding: '12px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    cancelBtn: { flex: 1, background: 'white', color: 'black', border: '4px solid black', padding: '12px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' }
  };

  const formModal = (title, onSave) => (
    <div style={styles.modal} onClick={closeForm}>
      <div style={styles.modalContent} onClick={event => event.stopPropagation()}>
        <h2 style={styles.modalTitle}>{title}</h2>

        <div style={styles.formGrid}>
          <div style={{ ...styles.row, ...styles.fullRow }}>
            <label style={styles.label}>UNIT # *</label>
            <input style={styles.input} value={form.unit_number} onChange={event => setForm({ ...form, unit_number: event.target.value })} maxLength={100} required />
          </div>
          <div style={styles.row}>
            <label style={styles.label}>YEAR</label>
            <input style={styles.input} type="number" value={form.year} onChange={event => setForm({ ...form, year: event.target.value })} min={1900} max={new Date().getFullYear() + 1} />
          </div>
          <div style={styles.row}>
            <label style={styles.label}>MAKE</label>
            <input style={styles.input} value={form.make} onChange={event => setForm({ ...form, make: event.target.value })} maxLength={100} />
          </div>
          <div style={styles.row}>
            <label style={styles.label}>MODEL</label>
            <input style={styles.input} value={form.model} onChange={event => setForm({ ...form, model: event.target.value })} maxLength={100} />
          </div>
          <div style={styles.row}>
            <label style={styles.label}>COLOR</label>
            <input style={styles.input} value={form.color} onChange={event => setForm({ ...form, color: event.target.value })} maxLength={100} />
          </div>
          <div style={{ ...styles.row, ...styles.fullRow }}>
            <label style={styles.label}>LICENSE PLATE</label>
            <input style={styles.input} value={form.license_plate_number} onChange={event => setForm({ ...form, license_plate_number: event.target.value })} maxLength={50} />
          </div>
          <label style={{ ...styles.checkboxRow, ...styles.fullRow }}>
            <input type="checkbox" checked={form.is_active} onChange={event => setForm({ ...form, is_active: event.target.checked })} />
            {form.is_active ? 'ACTIVE' : 'INACTIVE'}
          </label>
        </div>

        <div style={styles.modalBtnRow}>
          <button className="jf-vehicles-hover" style={styles.confirmBtn} onClick={onSave} disabled={saving}>
            {saving ? 'SAVING...' : 'SAVE'}
          </button>
          <button className="jf-vehicles-hover" style={styles.cancelBtn} onClick={closeForm} disabled={saving}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <style>{`
        .jf-vehicles-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
      `}</style>
      <div style={styles.contentWrapper}>
        <TitleBar
          icon="🚗"
          title="VEHICLES"
          accent="#E8761D"
          right={
            <button
              className="jf-vehicles-hover"
              onClick={() => navigate('/settings')}
              style={{ background: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING VEHICLES...</div>
          ) : (
            <>
              <div style={styles.note}>Manage optional vehicles used for mileage tracking.</div>

              <button className="jf-vehicles-hover" style={styles.addBtn} onClick={openAddForm}>
                + ADD VEHICLE
              </button>

              {vehicles.length === 0 ? (
                <div style={{ ...styles.note, backgroundColor: 'white' }}>No vehicles added yet.</div>
              ) : (
                vehicles.map(vehicle => {
                  const details = [vehicle.year, vehicle.make, vehicle.model, vehicle.color].filter(Boolean).join(' ');
                  const isActive = Number(vehicle.is_active) === 1;
                  return (
                    <div key={vehicle.id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <div style={styles.cardInfo}>
                          <div style={styles.cardTitle}>UNIT # {vehicle.unit_number}</div>
                          {details && <div style={styles.vehicleDetails}>{details}</div>}
                          {vehicle.license_plate_number && (
                            <div style={styles.vehicleDetails}>LICENSE PLATE: {vehicle.license_plate_number}</div>
                          )}
                          <div style={{ ...styles.status, backgroundColor: isActive ? '#dcfce7' : '#e5e7eb' }}>
                            {isActive ? 'ACTIVE' : 'INACTIVE'}
                          </div>
                        </div>
                        <div style={styles.cardActions}>
                          <button className="jf-vehicles-hover" style={styles.editBtn} onClick={() => startEdit(vehicle)}>EDIT</button>
                          <button className="jf-vehicles-hover" style={styles.deleteBtn} onClick={() => deleteVehicle(vehicle)}>DELETE</button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>

        {showAddVehicle && formModal('ADD VEHICLE', createVehicle)}
        {editingVehicleId && formModal('EDIT VEHICLE', updateVehicle)}
      </div>
    </div>
  );
};

export default VehiclesSettings;
